import { useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as PE } from 'react'
import type { AndarNaPlanta, EquipeNaPlanta, SalaNaPlanta } from './PlantaDoPredio.tsx'

/**
 * O lugar visto de fora (SPEC-20261010-1813): afastando o tabuleiro até o fim,
 * o escritório vira a construção inteira em pixel art, à noite — uma casa
 * (1 andar), um prédio (até 4) ou um edifício (5 ou mais). Cada andar é uma
 * faixa de janelas; janela acesa é sala com gente em chamada, e o filete
 * embaixo da janela é a cor da equipe dona da sala. A rodinha aproxima e
 * afasta onde está o mouse e arrastar move; clicar num andar (ou aproximar
 * muito nele) entra nele.
 */

type Alvo = { salaId?: string }
type Vista = { s: number; ox: number; oy: number }
export type Construcao = 'casa' | 'predio' | 'edificio'
export const construcaoPara = (andares: number): Construcao => (andares <= 1 ? 'casa' : andares <= 4 ? 'predio' : 'edificio')

const MERGULHO = 9
const ANDAR_H = 30
const TERREO_H = 26
const CHAO_H = 26

type Paleta = { parede: string; sombra: string; laje: string; moldura: string; vidroApagado: string; brilho: string }
const PALETAS: Record<Construcao, Paleta> = {
  casa: { parede: '#e7d9bd', sombra: '#cbb994', laje: '#b49a72', moldura: '#f6efe0', vidroApagado: '#2a3552', brilho: '#3a4a70' },
  predio: { parede: '#c7a789', sombra: '#a98a6e', laje: '#7d6450', moldura: '#efe3d2', vidroApagado: '#223050', brilho: '#34466e' },
  edificio: { parede: '#43597d', sombra: '#34476a', laje: '#25324d', moldura: '#8aa3c8', vidroApagado: '#1b2742', brilho: '#2d4068' },
}

type Faixa = { a: AndarNaPlanta; y: number; salas: SalaNaPlanta[]; gente: number }

function desenhar(c: HTMLCanvasElement, tipo: Construcao, faixas: Faixa[], W: number, H: number, x0: number, larg: number, topo: number, equipeDe: Map<string, EquipeNaPlanta>) {
  c.width = W
  c.height = H
  const g = c.getContext('2d') as CanvasRenderingContext2D
  g.imageSmoothingEnabled = false
  const P = PALETAS[tipo]
  const r = (x: number, y: number, w: number, h: number, cor: string) => {
    g.fillStyle = cor
    g.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h))
  }
  // semente fixa: estrelas e janelinhas sempre no mesmo lugar
  let s = 7
  const rnd = () => ((s = (s * 9301 + 49297) % 233280) / 233280)

  // céu da noite com estrelas e a lua
  for (let i = 0; i < 70; i++) r(rnd() * W, rnd() * (H * 0.62), 1, 1, rnd() > 0.7 ? '#fff6d8' : '#7f8fc0')
  r(W - 40, 18, 10, 10, '#f3ecd0')
  r(W - 42, 20, 2, 6, '#f3ecd0')
  r(W - 30, 20, 2, 6, '#f3ecd0')
  r(W - 37, 22, 3, 3, '#d9cfa8')

  // a cidade apagada lá atrás
  const chao = H - CHAO_H
  for (let x = 0; x < W; ) {
    const w = 14 + Math.floor(rnd() * 22)
    const h = 30 + Math.floor(rnd() * 70)
    if (x + w < x0 - 6 || x > x0 + larg + 6) {
      r(x, chao - h, w, h, '#1d2440')
      for (let yy = chao - h + 5; yy < chao - 6; yy += 7) for (let xx = x + 3; xx < x + w - 3; xx += 5) if (rnd() > 0.82) r(xx, yy, 2, 3, '#3d4a78')
    }
    x += w + 2
  }

  // calçada, rua e árvores
  r(0, chao, W, 6, '#6d7590')
  r(0, chao + 6, W, 1, '#4c5370')
  r(0, chao + 7, W, CHAO_H - 7, '#2b3047')
  for (let x = 4; x < W; x += 22) r(x, chao + 15, 10, 2, '#8b8f9e')
  const arvore = (x: number) => {
    r(x + 5, chao - 10, 3, 10, '#5b3d27')
    r(x, chao - 24, 13, 14, '#2f6b45')
    r(x + 2, chao - 28, 9, 5, '#3c8456')
    r(x + 2, chao - 22, 4, 4, '#4d9a63')
  }
  arvore(x0 - 26)
  arvore(x0 + larg + 12)
  // poste aceso
  const poste = (x: number) => {
    r(x, chao - 30, 2, 30, '#3a3f55')
    r(x - 2, chao - 32, 6, 3, '#3a3f55')
    r(x - 1, chao - 29, 4, 2, '#ffe7a3')
  }
  poste(x0 - 44)
  poste(x0 + larg + 34)

  // térreo com a entrada
  const tY = chao - TERREO_H
  r(x0 - 3, tY, larg + 6, TERREO_H, P.sombra)
  r(x0, tY, larg, TERREO_H, P.parede)
  const portaX = x0 + larg / 2 - 11
  r(portaX - 2, tY + 6, 26, TERREO_H - 6, P.moldura)
  r(portaX, tY + 8, 22, TERREO_H - 8, '#ffd27a')
  r(portaX + 10, tY + 8, 2, TERREO_H - 8, P.moldura)
  r(portaX - 8, tY + 2, 38, 3, '#1a2140')
  r(x0 + 8, tY + 8, 18, 12, '#ffd27a')
  r(x0 + larg - 26, tY + 8, 18, 12, '#ffd27a')
  r(portaX - 6, chao - 1, 34, 1, '#f3d9a0')

  // os andares, de baixo para cima
  for (const f of faixas) {
    const y = f.y
    r(x0 - 3, y + ANDAR_H - 3, larg + 6, 3, P.laje)
    r(x0, y, larg, ANDAR_H - 3, P.parede)
    if (tipo !== 'edificio') for (let yy = y + 4; yy < y + ANDAR_H - 3; yy += 4) r(x0, yy, larg, 1, P.sombra)
    // janelas: uma por sala do andar (no mínimo 4), acesas onde tem gente
    const salas = f.salas.filter((x) => !x.central)
    const central = f.salas.find((x) => x.central)
    const n = Math.max(4, salas.length)
    const jw = tipo === 'edificio' ? Math.floor((larg - 8) / n) - 2 : 14
    const vao = (larg - n * jw) / (n + 1)
    for (let i = 0; i < n; i++) {
      const sala = salas[i]
      const jx = x0 + vao + i * (jw + vao)
      const jy = y + 6
      const jh = ANDAR_H - 15
      const acesa = (sala?.pessoas ?? 0) > 0
      r(jx - 1, jy - 1, jw + 2, jh + 2, P.moldura)
      r(jx, jy, jw, jh, acesa ? '#ffd27a' : P.vidroApagado)
      if (acesa) {
        r(jx + 1, jy + 1, jw - 2, 2, '#fff1c2')
        // gente na janela: cabecinhas em silhueta
        const cabecas = Math.min(3, sala?.pessoas ?? 0)
        for (let k = 0; k < cabecas; k++) {
          const cx = jx + 2 + k * Math.max(4, Math.floor((jw - 4) / 3))
          r(cx, jy + jh - 6, 3, 3, '#7a5530')
          r(cx - 1, jy + jh - 3, 5, 3, '#7a5530')
        }
      } else {
        r(jx + 1, jy + 1, 2, jh - 2, P.brilho)
      }
      if (tipo !== 'edificio') r(jx + Math.floor(jw / 2), jy, 1, jh, P.moldura)
      const eq = sala?.teamId ? equipeDe.get(sala.teamId) : undefined
      if (eq) r(jx - 1, jy + jh + 2, jw + 2, 2, eq.color)
    }
    // a área central acesa vira a luz do corredor no andar
    if (central && central.pessoas) r(x0 + 2, y + ANDAR_H - 6, larg - 4, 1, '#ffcf6a')
  }

  // telhado
  const alto = faixas.length ? (faixas[faixas.length - 1] as Faixa).y : tY
  if (tipo === 'casa') {
    const meio = x0 + larg / 2
    for (let i = 0; i < 34; i++) {
      const w = larg + 16 - i * ((larg + 16) / 34)
      r(meio - w / 2, alto - i - 1, w, 1, i % 4 === 0 ? '#8c3b2a' : '#a8483a')
    }
    r(x0 + larg - 40, alto - 34, 9, 18, '#8a7a66')
    r(x0 + larg - 42, alto - 36, 13, 3, '#6d604f')
  } else {
    r(x0 - 4, alto - 5, larg + 8, 5, P.laje)
    r(x0 - 4, alto - 7, larg + 8, 2, P.moldura)
    const ax = x0 + larg - 24
    r(ax, alto - (tipo === 'edificio' ? 40 : 26), 2, tipo === 'edificio' ? 33 : 19, '#8b93a8')
    r(ax - 1, alto - (tipo === 'edificio' ? 42 : 28), 4, 3, '#ff5a5a')
    r(x0 + 14, alto - 14, 22, 7, '#525a73')
    r(x0 + 16, alto - 13, 18, 1, '#6c7593')
    if (tipo === 'edificio') r(x0 + larg / 2 - 20, alto - 12, 40, 5, '#2b3a5c')
  }
  void topo
}

export function PredioPorFora({
  andares,
  salas,
  equipes,
  atual,
  minhaSala,
  nome,
  onEntrar,
}: {
  andares: AndarNaPlanta[]
  salas: SalaNaPlanta[]
  equipes: EquipeNaPlanta[]
  atual: string | null
  minhaSala: string | null
  nome: string
  onEntrar: (floorId: string, alvo: Alvo, ponto: { x: number; y: number }) => void
}) {
  const caixa = useRef<HTMLDivElement>(null)
  const tela = useRef<HTMLCanvasElement>(null)
  const [vista, setVista] = useState<Vista>({ s: 3, ox: 0, oy: 0 })
  const [sobre, setSobre] = useState<string | null>(null)
  const mexeu = useRef(false)
  const mergulhou = useRef(false)
  const arrasto = useRef<{ x: number; y: number; ox: number; oy: number; moveu: boolean } | null>(null)
  const equipeDe = useMemo(() => new Map(equipes.map((e) => [e.id, e])), [equipes])

  const tipo = construcaoPara(andares.length)
  const larg = tipo === 'casa' ? 200 : tipo === 'predio' ? 210 : 150
  const W = Math.max(larg + 180, 360)
  const topoLivre = tipo === 'casa' ? 60 : tipo === 'edificio' ? 70 : 55
  const H = topoLivre + andares.length * ANDAR_H + TERREO_H + CHAO_H
  const x0 = Math.round((W - larg) / 2)

  const faixas = useMemo<Faixa[]>(() => {
    const chao = H - CHAO_H - TERREO_H
    return andares.map((a, i) => {
      const doAndar = salas.filter((s) => s.floorId === a.id).sort((p, q) => p.lot - q.lot)
      return { a, y: chao - (i + 1) * ANDAR_H, salas: doAndar, gente: doAndar.reduce((n, s) => n + s.pessoas, 0) }
    })
  }, [andares, salas, H])

  useEffect(() => {
    if (tela.current) desenhar(tela.current, tipo, faixas, W, H, x0, larg, topoLivre, equipeDe)
  }, [tipo, faixas, W, H, x0, larg, topoLivre, equipeDe])

  const enquadrar = () => {
    const el = caixa.current
    if (!el) return
    const s = Math.max(1, Math.min((el.clientWidth - 60) / W, (el.clientHeight - 90) / H))
    setVista({ s, ox: (el.clientWidth - W * s) / 2, oy: (el.clientHeight - H * s) / 2 + 20 })
  }
  useLayoutEffect(() => {
    if (!mexeu.current) enquadrar()
    const el = caixa.current
    if (!el) return
    const ro = new ResizeObserver(() => !mexeu.current && enquadrar())
    ro.observe(el)
    return () => ro.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [W, H])

  const andarEm = (cx: number, cy: number, v: Vista) => {
    const wx = (cx - v.ox) / v.s
    const wy = (cy - v.oy) / v.s
    if (wx < x0 - 4 || wx > x0 + larg + 4) return null
    return faixas.find((f) => wy >= f.y && wy < f.y + ANDAR_H) ?? null
  }

  useEffect(() => {
    const el = caixa.current
    if (!el) return
    const roda = (e: WheelEvent) => {
      e.preventDefault()
      mexeu.current = true
      const r = el.getBoundingClientRect()
      const cx = e.clientX - r.left
      const cy = e.clientY - r.top
      setVista((v) => {
        const s = Math.max(0.6, Math.min(16, v.s * (e.deltaY < 0 ? 1.15 : 1 / 1.15)))
        const nova = { s, ox: cx - (cx - v.ox) * (s / v.s), oy: cy - (cy - v.oy) * (s / v.s) }
        // chegou bem perto de um andar: entra nele
        if (e.deltaY < 0 && s >= MERGULHO && !mergulhou.current) {
          const f = andarEm(cx, cy, nova)
          if (f) {
            mergulhou.current = true
            window.setTimeout(() => onEntrar(f.a.id, {}, { x: e.clientX, y: e.clientY }), 0)
          }
        }
        return nova
      })
    }
    el.addEventListener('wheel', roda, { passive: false })
    return () => el.removeEventListener('wheel', roda)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faixas, onEntrar, x0, larg])

  const apertar = (e: PE<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return
    arrasto.current = { x: e.clientX, y: e.clientY, ox: vista.ox, oy: vista.oy, moveu: false }
  }
  const mover = (e: PE<HTMLDivElement>) => {
    const el = caixa.current
    const a = arrasto.current
    if (a) {
      const dx = e.clientX - a.x
      const dy = e.clientY - a.y
      if (!a.moveu && Math.hypot(dx, dy) > 4) {
        a.moveu = true
        mexeu.current = true
        e.currentTarget.setPointerCapture(e.pointerId)
      }
      if (a.moveu) setVista((v) => ({ ...v, ox: a.ox + dx, oy: a.oy + dy }))
      return
    }
    if (!el) return
    const r = el.getBoundingClientRect()
    setSobre(andarEm(e.clientX - r.left, e.clientY - r.top, vista)?.a.id ?? null)
  }
  const soltar = (e: PE<HTMLDivElement>) => {
    const a = arrasto.current
    arrasto.current = null
    if (!a || a.moveu || !caixa.current) return
    const r = caixa.current.getBoundingClientRect()
    const f = andarEm(e.clientX - r.left, e.clientY - r.top, vista)
    if (f) onEntrar(f.a.id, {}, { x: e.clientX, y: e.clientY })
  }

  const nomeDoTipo = tipo === 'casa' ? 'A casa' : tipo === 'predio' ? 'O prédio' : 'O edifício'
  return (
    <div className={`esc__fora${sobre ? ' esc__fora--sobre' : ''}`} ref={caixa} onPointerDown={apertar} onPointerMove={mover} onPointerUp={soltar} onPointerLeave={() => setSobre(null)}>
      <div className="esc__fora-mundo" style={{ transform: `translate(${vista.ox}px, ${vista.oy}px) scale(${vista.s})`, width: W, height: H }}>
        <canvas ref={tela} className="esc__fora-tela" style={{ width: W, height: H }} role="img" aria-label={`${nomeDoTipo} visto de fora, com ${andares.length} ${andares.length === 1 ? 'andar' : 'andares'}`} />
        {faixas.map((f) => (
          <div
            key={f.a.id}
            className={`esc__fora-andar${f.a.id === sobre ? ' esc__fora-andar--sobre' : ''}${f.a.id === atual ? ' esc__fora-andar--atual' : ''}`}
            style={{ left: x0 - 3, top: f.y, width: larg + 6, height: ANDAR_H }}
          />
        ))}
        {faixas.map((f) => (
          <span key={`p-${f.a.id}`} className="esc__fora-placa" style={{ left: x0 + larg + 8, top: f.y + ANDAR_H / 2, ['--inv' as string]: String(1 / vista.s) }}>
            <b>{f.a.name}</b>
            {f.gente ? <em>{f.gente}</em> : null}
            {f.salas.some((s) => s.id === minhaSala) ? <i className="esc__fora-voce">você</i> : null}
          </span>
        ))}
        <span className="esc__fora-letreiro" style={{ left: x0 + larg / 2, top: H - CHAO_H - TERREO_H + 3.5, ['--inv' as string]: String(1 / vista.s) }}>
          {nome}
        </span>
      </div>
      <p className="esc__planta-ajuda">Janela acesa: tem gente em chamada. Clique num andar para entrar; gire a rodinha para aproximar ou afastar.</p>
      <button
        type="button"
        className="esc__planta-centro"
        onClick={() => {
          mexeu.current = false
          enquadrar()
        }}
      >
        <i className="fas fa-compress-arrows-alt" aria-hidden /> Ver tudo
      </button>
    </div>
  )
}
